import React, { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../../config';

function getCurrentMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function formatNumber(num) {
  return (Math.round((num || 0) * 100) / 100).toLocaleString('cs-CZ');
}

/**
 * MultisportTab — "Multisport"
 * Obchodník vidí pouze cenu, kterou platí (dle svého GP a nastavených pásem).
 * Admin: přiřazuje Multisport uživatelům + spravuje pásma GP → cena ("Multisport - nastavení").
 */
export default function MultisportTab({ userRole, users, currentUserId, isDarkMode }) {
  const isAdmin = userRole === 'admin';
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [selectedUser, setSelectedUser] = useState(isAdmin ? '' : currentUserId);
  const [data, setData] = useState({ enabled: false, gp: 0, price: 0, tiers: [] });
  const [loading, setLoading] = useState(false);
  const [toggling, setToggling] = useState(false);

  // Nastavení pásem (admin)
  const [showSettings, setShowSettings] = useState(false);
  const [tiers, setTiers] = useState([]);
  const [savingTiers, setSavingTiers] = useState(false);

  // Přehled přiřazení (admin) – lokální override po přepnutí, aby byl přehled hned aktuální
  const [showOverview, setShowOverview] = useState(true);
  const [overviewSearch, setOverviewSearch] = useState('');
  const [assignOverrides, setAssignOverrides] = useState({}); // { userId: bool }

  const availableUsers = isAdmin ? (users || []) : [];

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const effectiveUser = isAdmin ? selectedUser : currentUserId;
      let url = `${API_URL}/multisport-cost?month=${selectedMonth}&userId=${currentUserId || ''}`;
      if (effectiveUser) url += `&filterUserId=${effectiveUser}`;
      const res = await fetch(url);
      const json = res.ok ? await res.json() : { enabled: false, gp: 0, price: 0, tiers: [] };
      setData(json);
    } catch {
      setData({ enabled: false, gp: 0, price: 0, tiers: [] });
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, selectedUser, currentUserId, isAdmin]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const loadTiers = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/multisport-config?userId=${currentUserId || ''}`);
      if (res.ok) {
        const json = await res.json();
        setTiers(json.tiers || []);
      }
    } catch { /* ignore */ }
  }, [currentUserId]);

  const handleToggleAssign = async () => {
    if (!isAdmin || !selectedUser) return;
    setToggling(true);
    try {
      const res = await fetch(`${API_URL}/users/${selectedUser}/multisport?userId=${currentUserId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: !data.enabled, actorUserId: currentUserId })
      });
      if (res.ok) {
        setAssignOverrides(prev => ({ ...prev, [selectedUser]: !data.enabled }));
        await fetchData();
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.error || 'Chyba při změně přiřazení Multisportu');
      }
    } catch {
      alert('Chyba připojení');
    } finally {
      setToggling(false);
    }
  };

  // Přepnutí přiřazení přímo z přehledu (bez nutnosti vybrat uživatele ve filtru)
  const toggleAssignForUser = async (uid, newEnabled) => {
    if (!isAdmin || !uid) return;
    // Optimistický update přehledu
    setAssignOverrides(prev => ({ ...prev, [uid]: newEnabled }));
    try {
      const res = await fetch(`${API_URL}/users/${uid}/multisport?userId=${currentUserId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: newEnabled, actorUserId: currentUserId })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.error || 'Chyba při změně přiřazení Multisportu');
        setAssignOverrides(prev => ({ ...prev, [uid]: !newEnabled })); // revert
        return;
      }
      // Pokud jde o právě vybraného uživatele, aktualizuj i hlavní kartu
      if (String(uid) === String(effectiveUser)) fetchData();
    } catch {
      alert('Chyba připojení');
      setAssignOverrides(prev => ({ ...prev, [uid]: !newEnabled })); // revert
    }
  };

  const openSettings = async () => {
    await loadTiers();
    setShowSettings(true);
  };

  const handleSaveTiers = async () => {
    setSavingTiers(true);
    try {
      const res = await fetch(`${API_URL}/multisport-config?userId=${currentUserId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tiers, actorUserId: currentUserId })
      });
      if (res.ok) {
        setShowSettings(false);
        await fetchData();
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.error || 'Chyba při ukládání nastavení');
      }
    } catch {
      alert('Chyba připojení');
    } finally {
      setSavingTiers(false);
    }
  };

  const updateTier = (idx, field, value) => {
    setTiers(prev => prev.map((t, i) => i === idx ? { ...t, [field]: value } : t));
  };
  const addTier = () => setTiers(prev => [...prev, { minGp: 0, maxGp: null, price: 0 }]);
  const removeTier = (idx) => setTiers(prev => prev.filter((_, i) => i !== idx));

  const card = `rounded-lg border p-6 ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'} shadow-sm`;
  const inputCls = `w-full border rounded px-3 py-2 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`;
  const th = `px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`;

  const effectiveUser = isAdmin ? selectedUser : currentUserId;

  return (
    <div>
      {/* Filtry */}
      <div className={`flex flex-wrap gap-4 items-end mb-6 p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
        <div>
          <label className={`block text-xs font-semibold mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Měsíc</label>
          <input
            type="month"
            value={selectedMonth}
            onChange={e => setSelectedMonth(e.target.value)}
            className={`border rounded px-2 py-1 text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300 text-gray-800'}`}
          />
        </div>
        {isAdmin && (
          <div>
            <label className={`block text-xs font-semibold mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Uživatel</label>
            <select
              value={selectedUser}
              onChange={e => setSelectedUser(e.target.value)}
              className={`border rounded px-2 py-1 text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300 text-gray-800'}`}
            >
              <option value="">— Vyberte uživatele —</option>
              {availableUsers.map(u => (
                <option key={u._id} value={u._id}>{u.name || u.email}</option>
              ))}
            </select>
          </div>
        )}
        {isAdmin && (
          <button
            onClick={openSettings}
            className="ml-auto text-sm px-3 py-2 rounded bg-indigo-600 hover:bg-indigo-700 text-white"
          >⚙️ Multisport - nastavení</button>
        )}
      </div>

      {loading ? (
        <div className={`text-center py-10 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Načítám…</div>
      ) : (isAdmin && !effectiveUser) ? (
        <div className={`text-center py-10 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Vyberte uživatele pro zobrazení Multisportu.</div>
      ) : (
        <div className={card}>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h3 className={`font-semibold text-lg mb-1 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>🏋️ Multisport</h3>
              {data.enabled ? (
                <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {isAdmin && <>GP za měsíc: <strong>{formatNumber(data.gp)} Kč</strong> · </>}
                  Cena, kterou platíte:
                </p>
              ) : (
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Multisport není {isAdmin ? 'tomuto uživateli ' : ''}přidělen.
                </p>
              )}
            </div>
            {data.enabled && (
              <div className="text-right">
                <div className="text-3xl font-bold text-pink-500">{formatNumber(data.price)} Kč</div>
                <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>/ měsíc</div>
              </div>
            )}
          </div>

          {/* Admin přiřazení */}
          {isAdmin && effectiveUser && (
            <div className={`mt-4 pt-4 border-t ${isDarkMode ? 'border-gray-600' : 'border-gray-200'} flex items-center gap-2`}>
              <div
                className={`flex items-center gap-2 ${toggling ? 'opacity-60 cursor-wait' : 'cursor-pointer'}`}
                onClick={handleToggleAssign}
                title="Přiřadit / odebrat Multisport uživateli"
              >
                <div className={`relative w-11 h-6 rounded-full transition-colors ${data.enabled ? 'bg-green-500' : isDarkMode ? 'bg-gray-600' : 'bg-gray-300'}`}>
                  <span className={`absolute left-0.5 top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${data.enabled ? 'translate-x-5' : 'translate-x-0'}`} />
                </div>
                <span className={`text-sm font-semibold ${data.enabled ? 'text-green-500' : isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Multisport přidělen: {data.enabled ? 'ANO' : 'NE'}
                </span>
              </div>
            </div>
          )}

          {data.enabled && (
            <p className={`text-xs mt-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Cena Multisportu se propisuje do shrnutí a faktury (odečteno z výplaty).
            </p>
          )}
        </div>
      )}

      {/* Přehled přiřazení Multisportu (admin) */}
      {isAdmin && (() => {
        const roleOrder = ['admin', 'obchodnik', 'callcentrum', 'podpora', 'teamleader', 'user', 'test'];
        const roleRank = (r) => { const i = roleOrder.indexOf(r); return i === -1 ? roleOrder.length : i; };
        const isOn = (u) => (u._id in assignOverrides ? assignOverrides[u._id] : !!u.multisportEnabled);
        const list = (users || [])
          .filter(u => {
            if (!overviewSearch.trim()) return true;
            const q = overviewSearch.toLowerCase();
            return (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
          })
          .sort((a, b) => {
            const ra = roleRank(a.role), rb = roleRank(b.role);
            if (ra !== rb) return ra - rb;
            return (a.name || a.email || '').localeCompare(b.name || b.email || '', 'cs', { sensitivity: 'base' });
          });
        const enabledCount = (users || []).filter(u => isOn(u)).length;
        const totalCount = (users || []).length;
        return (
          <div className={`${card} mt-6`}>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div className="flex items-center gap-3">
                <h3 className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>👥 Přehled přiřazení Multisportu</h3>
                <span className={`text-xs px-2 py-1 rounded-full font-semibold ${isDarkMode ? 'bg-green-800 text-green-100' : 'bg-green-100 text-green-700'}`}>Zapnuto: {enabledCount}</span>
                <span className={`text-xs px-2 py-1 rounded-full font-semibold ${isDarkMode ? 'bg-gray-600 text-gray-200' : 'bg-gray-200 text-gray-700'}`}>Vypnuto: {totalCount - enabledCount}</span>
              </div>
              <button
                onClick={() => setShowOverview(v => !v)}
                className={`text-sm px-3 py-1.5 rounded ${isDarkMode ? 'bg-gray-600 hover:bg-gray-500 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
              >{showOverview ? 'Skrýt' : 'Zobrazit'}</button>
            </div>

            {showOverview && (
              <>
                <input
                  type="text"
                  value={overviewSearch}
                  onChange={e => setOverviewSearch(e.target.value)}
                  placeholder="🔍 Hledat uživatele…"
                  className={`w-full sm:w-72 mb-3 border rounded px-3 py-2 text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'}`}
                />
                <div className={`rounded-lg border overflow-hidden ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                  <table className="w-full text-sm">
                    <thead className={isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}>
                      <tr>
                        <th className={th}>Uživatel</th>
                        <th className={th}>Role</th>
                        <th className={`${th} text-center`}>Stav</th>
                        <th className={`${th} text-right`}>Akce</th>
                      </tr>
                    </thead>
                    <tbody>
                      {list.length === 0 ? (
                        <tr><td colSpan={4} className={`px-3 py-6 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Žádní uživatelé.</td></tr>
                      ) : list.map(u => {
                        const on = isOn(u);
                        return (
                          <tr key={u._id} className={`border-t ${isDarkMode ? 'border-gray-700 hover:bg-gray-700/50' : 'border-gray-100 hover:bg-gray-50'}`}>
                            <td className={`px-3 py-2 font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>{u.name || u.email || '—'}</td>
                            <td className={`px-3 py-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{u.role || '—'}</td>
                            <td className="px-3 py-2 text-center">
                              <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${on ? (isDarkMode ? 'bg-green-800 text-green-100' : 'bg-green-100 text-green-700') : (isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600')}`}>
                                {on ? 'Zapnuto' : 'Vypnuto'}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-right">
                              <div className="flex justify-end">
                                <div
                                  className="flex items-center gap-2 cursor-pointer"
                                  onClick={() => toggleAssignForUser(u._id, !on)}
                                  title="Přepnout přiřazení Multisportu"
                                >
                                  <div className={`relative w-11 h-6 rounded-full transition-colors ${on ? 'bg-green-500' : isDarkMode ? 'bg-gray-600' : 'bg-gray-300'}`}>
                                    <span className={`absolute left-0.5 top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${on ? 'translate-x-5' : 'translate-x-0'}`} />
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        );
      })()}

      {/* Modal nastavení pásem */}
      {showSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden m-4`}>
            <div className={`flex items-center justify-between p-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Multisport - nastavení pásem</h2>
              <button onClick={() => setShowSettings(false)} className={isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}>✕</button>
            </div>
            <div className="p-4 overflow-auto max-h-[calc(90vh-140px)]">
              <p className={`text-sm mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Cena Multisportu podle GP obchodníka za měsíc. Pásmo platí pro GP ≥ Od a &lt; Do (Do prázdné = neomezeno).
              </p>
              <table className="w-full border-collapse mb-4">
                <thead>
                  <tr className={`border-b ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                    <th className={th}>GP od (Kč)</th>
                    <th className={th}>GP do (Kč)</th>
                    <th className={th}>Cena (Kč)</th>
                    <th className={th}></th>
                  </tr>
                </thead>
                <tbody>
                  {tiers.map((t, idx) => (
                    <tr key={idx} className={`border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                      <td className="px-2 py-2">
                        <input type="number" value={t.minGp ?? 0} onChange={e => updateTier(idx, 'minGp', e.target.value)} className={inputCls} />
                      </td>
                      <td className="px-2 py-2">
                        <input type="number" value={t.maxGp ?? ''} placeholder="neomezeno" onChange={e => updateTier(idx, 'maxGp', e.target.value === '' ? null : e.target.value)} className={inputCls} />
                      </td>
                      <td className="px-2 py-2">
                        <input type="number" value={t.price ?? 0} onChange={e => updateTier(idx, 'price', e.target.value)} className={inputCls} />
                      </td>
                      <td className="px-2 py-2 text-center">
                        <button onClick={() => removeTier(idx)} className="text-red-500 hover:text-red-700 text-lg" title="Odebrat pásmo">🗑</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button onClick={addTier} className={`text-sm px-3 py-1.5 rounded ${isDarkMode ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}>+ Přidat pásmo</button>
            </div>
            <div className={`flex justify-end gap-2 p-4 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <button onClick={() => setShowSettings(false)} className={`px-4 py-2 rounded ${isDarkMode ? 'bg-gray-600 text-white hover:bg-gray-500' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}>Zrušit</button>
              <button onClick={handleSaveTiers} disabled={savingTiers} className="px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50">
                {savingTiers ? 'Ukládám…' : 'Uložit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
